import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CreerAdminPiloteTenantSSOModel, CreerAdminPiloteTenantSSOItem } from 'src/models/pilotage/creerAdminPiloteTenantSSOModel';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { tap } from 'rxjs';
import * as XLSX from 'xlsx';

import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ContexteUtilisateur, EtiquetteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { ResultCreerUtilisateursTenantSSOModel } from 'src/models/pilotage/resultCreerUtilisateurTenantSSOModel';
import { ConfirmationCreationUtilisateurDialogComponent } from '../confirmation-creation-utilisateur-dialog/confirmation-creation-utilisateur-dialog.component';

@Component({
    selector: 'app-creer-admin-pilote-tenant-sso-dialog',
    templateUrl: './creer-admin-pilote-tenant-sso-dialog.component.html',
    styleUrls: ['./creer-admin-pilote-tenant-sso-dialog.component.scss'],
    imports: [
    FormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    TranslateModule
]
})
export class CreerAdminPiloteTenantSSODialogComponent implements OnInit {
    entiteVisioSelectionnee: number = 0;
    entites: any[] = [];
    entiteUnique: { id: number, nom: string } | null = null;
    utilisateurManuel: CreerAdminPiloteTenantSSOItem = { prenom: '', nom: '', courriel: '' };
    fichierSelectionne: File | null = null;
    methodeCreation: 'excel' | 'manual' = 'manual';
    utilisateursExcel: CreerAdminPiloteTenantSSOItem[] = [];
    chargementEnCours: boolean = false;

    etiquettesDisponibles: EtiquetteModel[] = [];
    etiquettesSelectionnees: EtiquetteModel[] = [];

    estInvitePilote = false;
    estInviteAdmin = false;
    estPiloteGroupe = false;
    inviteEntiteAdmins: { id: number, nom: string }[] = [];
    entiteIdInviteAdminSelectionnee: number | null = null;
    entitesDispoInviteAdmin: { id: number, nom: string }[] = [];

    constructor(
        private dialogRef: MatDialogRef<CreerAdminPiloteTenantSSODialogComponent>,
        private utilisateurService: UtilisateurService,
        public etiquetteService: EtiquetteService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.chargerEntites();
    }

    chargerEntites(): void {
        this.entites = this.utilisateurService.contexteUtilisateur.entitesAccessibles
            .filter(re => !re.estGroupe)
            .map((e: any) => ({ id: e.entiteId, nom: e.nom }));

        if (this.entites.length === 1) {
            this.entiteUnique = this.entites[0];
            this.entiteVisioSelectionnee = this.entiteUnique!.id;
        } else if (this.utilisateurService.entiteUtilisateurSelectionnee) {
            this.entiteVisioSelectionnee = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
        }

        this.estPiloteGroupe = this.utilisateurService.contexteUtilisateur.entitesAccessibles
            .some(re => re.estGroupe);

        this.majEntiteDispoInviteAdmin();
        this.mettreAJourEtiquettesDisponibles();
    }

    majEntiteDispoInviteAdmin(): void {
        this.entitesDispoInviteAdmin = this.entites.filter(
            entite => !this.inviteEntiteAdmins.some(selected => selected.id === entite.id)
        );
        this.entiteIdInviteAdminSelectionnee = null;
    }

    mettreAJourEtiquettesDisponibles(): void {
        const groupEntites = this.utilisateurService.contexteUtilisateur.toutesLesEntites
            .filter(e => e.estGroupe)
            .map(e => e.entiteId);

        const etiquettesGroupe = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
            .filter(e => e.entiteIds.some(id => groupEntites.includes(id)));

        const selectedEntiteIds = [this.entiteVisioSelectionnee, ...this.inviteEntiteAdmins.map(e => e.id)];

        const etiquettesEntites = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
            .filter(e => e.entiteIds.some(id => selectedEntiteIds.includes(id)));

        const nouvellesEtiquettesAccessibles = [...new Set([...etiquettesGroupe, ...etiquettesEntites])];

        this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(etiquette =>
            nouvellesEtiquettesAccessibles.some(etiquetteAccessible => etiquetteAccessible.etiquetteId === etiquette.etiquetteId)
        );

        this.etiquettesDisponibles = nouvellesEtiquettesAccessibles
            .filter(etiquette => !this.etiquettesSelectionnees.some(selected => selected.etiquetteId === etiquette.etiquetteId))
            .sort((a, b) =>
                this.etiquetteService.getNomLocalise(a.locales).localeCompare(
                    this.etiquetteService.getNomLocalise(b.locales)
                )
            );
    }

    onEtiquetteSelectionnee(event: Event, selectElement: HTMLSelectElement): void {
        const etiquetteId = Number(selectElement.value);
        const etiquetteSelectionnee = this.etiquettesDisponibles.find(e => e.etiquetteId === etiquetteId);
        if (etiquetteSelectionnee) {
            this.etiquettesSelectionnees.push(etiquetteSelectionnee);
            this.etiquettesDisponibles = this.etiquettesDisponibles.filter(e => e.etiquetteId !== etiquetteId);
            selectElement.value = '';
        }
    }

    supprimerEtiquetteSelectionnee(id: number): void {
        const removedEtiquette = this.etiquettesSelectionnees.find(e => e.etiquetteId === id);
        this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(e => e.etiquetteId !== id);
        if (removedEtiquette) {
            this.etiquettesDisponibles.push(removedEtiquette);
            this.etiquettesDisponibles.sort((a, b) =>
                this.etiquetteService.getNomLocalise(a.locales).localeCompare(
                    this.etiquetteService.getNomLocalise(b.locales)
                )
            );
        }
    }

    onChangementRole(role: 'pilote' | 'admin'): void {
        if (role === 'pilote') {
            if (this.estInvitePilote) {
                this.estInviteAdmin = false;
                this.inviteEntiteAdmins = [];
            }
        } else {
            if (this.estInviteAdmin) {
                this.estInvitePilote = false;
                if (this.entites.length === 1) {
                    this.inviteEntiteAdmins.push(this.entites[0]);
                }
            } else {
                this.inviteEntiteAdmins = [];
            }
        }
        this.majEntiteDispoInviteAdmin();
        this.mettreAJourEtiquettesDisponibles();
    }

    onEntiteAdminSelectionnee(event: Event): void {
        const selectElement = event.target as HTMLSelectElement;
        const entiteId = Number(selectElement.value);
        const entiteSelectionne = this.entitesDispoInviteAdmin.find(e => e.id === entiteId);
        if (entiteSelectionne) {
            this.inviteEntiteAdmins.push(entiteSelectionne);
            this.majEntiteDispoInviteAdmin();
            this.mettreAJourEtiquettesDisponibles();
        }
    }

    supprimerEntiteSelectionnee(id: number): void {
        this.inviteEntiteAdmins = this.inviteEntiteAdmins.filter(entite => entite.id !== id);
        this.majEntiteDispoInviteAdmin();
        this.mettreAJourEtiquettesDisponibles();
    }

    telechargerModeleExcel(): void {
        this.utilisateurService.obtenirModeleUtilisateurTenantSSO().pipe(
            tap(
                (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'modeleTenant.xlsx';
                    link.click();
                    window.URL.revokeObjectURL(url);
                },
                (error: any) => {
                    console.error('Error downloading file:', error);
                }
            )
        ).subscribe();
    }

    onFichierSelectionne(event: any) {
        const file = event.target.files[0];
        this.fichierSelectionne = file;
        const reader = new FileReader();

        reader.onload = (e: any) => {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const excelData = XLSX.utils.sheet_to_json(worksheet);

            this.utilisateursExcel = excelData
                .map((row: any) => ({
                    prenom: row.Prenom,
                    nom: row.Nom,
                    courriel: row.Courriel || row.Email
                }))
                .filter(user => user.prenom && user.nom && user.courriel);
        };

        reader.readAsBinaryString(file);
    }

    boutonCreerDesactive(): boolean {
        if (!this.estInvitePilote && !this.estInviteAdmin) return true;
        if (this.estInviteAdmin && this.inviteEntiteAdmins.length === 0) return true;
        if (this.entiteVisioSelectionnee === 0) return true;

        if (this.methodeCreation === 'manual') {
            return !this.utilisateurManuel.prenom?.trim() || !this.utilisateurManuel.nom?.trim() || !this.utilisateurManuel.courriel?.trim();
        }

        if (this.methodeCreation === 'excel') {
            return this.fichierSelectionne === null || this.utilisateursExcel.length === 0;
        }
        return false;
    }

    private readonly TAILLE_LOT = 5;

    onAnnuler(): void {
        this.dialogRef.close(false);
    }

    onConfirmer(): void {
        this.chargementEnCours = true;

        const utilisateurs = this.methodeCreation === 'manual'
            ? [this.utilisateurManuel]
            : this.utilisateursExcel;

        const lots = this.decoupeEnLots(utilisateurs, this.TAILLE_LOT);
        const resultAggrege: ResultCreerUtilisateursTenantSSOModel = {
            utilisateursSucces: [],
            utilisateursDejaPresents: [],
            utilisateursEnErreurs: []
        };

        this.envoyerLot(0, lots, resultAggrege);
    }

    private envoyerLot(index: number, lots: CreerAdminPiloteTenantSSOItem[][], resultAggrege: ResultCreerUtilisateursTenantSSOModel): void {
        if (index >= lots.length) {
            this.chargementEnCours = false;
            this.dialogRef.close(true);
            this.dialog.open(ConfirmationCreationUtilisateurDialogComponent, { data: resultAggrege });
            return;
        }

        const model: CreerAdminPiloteTenantSSOModel = {
            entiteVisioId: this.entiteVisioSelectionnee,
            utilisateurs: lots[index],
            etiquetteIds: this.etiquettesSelectionnees.map(e => e.etiquetteId),
            groupePiloteId: this.estInvitePilote ? this.utilisateurService.contexteUtilisateur.entitesAccessibles.find(re => re.estGroupe)?.entiteId! : null,
            entiteAdminIds: this.estInviteAdmin ? this.inviteEntiteAdmins.map(er => er.id) : [],
            codeLangue: this.utilisateurService.obtenirLangueCourante()
        };

        this.utilisateurService.creerAdminsPilotesTenantSSO(model).subscribe({
            next: (result) => {
                resultAggrege.utilisateursSucces.push(...result.utilisateursSucces);
                resultAggrege.utilisateursDejaPresents.push(...result.utilisateursDejaPresents);
                resultAggrege.utilisateursEnErreurs.push(...result.utilisateursEnErreurs);
                this.envoyerLot(index + 1, lots, resultAggrege);
            },
            error: () => {
                this.chargementEnCours = false;
                this.dialogRef.close(false);
            }
        });
    }

    private decoupeEnLots<T>(items: T[], taille: number): T[][] {
        const lots: T[][] = [];
        for (let i = 0; i < items.length; i += taille) {
            lots.push(items.slice(i, i + taille));
        }
        return lots;
    }

    onEntiteVisioSelectionnee(event: any): void {
        this.entiteVisioSelectionnee = parseInt(event.target.value);
        this.mettreAJourEtiquettesDisponibles();
    }
}
